<template>
  <div class="border_8 rounded-lg mt-4 relative">
    <div class="flex items-start justify-end mb-3 w-fit absolute top-6 right-6">
      <div class="cursor-pointer hover:opacity-70" @click="handleRemove">
        <ThemeImage
          :icon="imageNames.CLOSE_GREY"
          alt="remove_condition"
          class="size-5"
        />
      </div>
    </div>
    <div class="p-4">
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-[15px] text_color">where</span>

        <a-select
          :value="conditionType || 'user_in_role'"
          :show-arrow="true"
          :disabled="disabled"
          @change="handleTypeChange"
          class="crm_condition_select"
          style="width: 280px"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>

          <a-select-option
            v-for="option in conditionTypeOptions"
            :key="option.value"
            :value="option.value"
          >
            <span class="proxima_nova text_color">{{ option.label }}</span>
          </a-select-option>
        </a-select>

        <span class="text-[15px] text_color"
          >is assigned to SuperAGI user property(s)</span
        >

        <a-select
          :value="assignedProperty || []"
          mode="multiple"
          :show-arrow="true"
          :allow-clear="true"
          :disabled="disabled || !userTypeProperties.length"
          placeholder="Select properties"
          @change="handlePropertyChange"
          class="crm_condition_select"
          style="width: 280px"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>

          <a-select-option value="__select_all__" class="select_all_option">
            <span class="proxima_nova text_color font-semibold"
              >Select All</span
            >
          </a-select-option>

          <a-select-option
            v-for="property in userTypeProperties"
            :key="property.slug"
            :value="property.slug"
          >
            <span class="proxima_nova text_color">{{ property.name }}</span>
          </a-select-option>
        </a-select>
      </div>
    </div>

    <div v-if="conditionType === 'specific_users_teams'" class="border_top p-4">
      <div class="flex gap-2">
        <UsersTeamsSelectDropdown
          v-model="usersAndTeamsModel"
          :options-by-tab="usersTeamsOptions"
          :initial-selection-by-tab="initialSelectionByTab"
          label="Add Users & Teams"
          :position="'start'"
          :dropdown-width="'400px'"
          :enable-auto-select-current-user="false"
          @selection-change="handleUsersTeamsChange"
        />
        <div class="flex items-center gap-1 px-2">
          <ThemeImage
            :icon="imageNames.WARNING_ICON"
            alt="warning_icon"
            class="size-3"
          />
          <span class="text-[13px] caution_message_color">
            <span class="font-semibold">
              {{
                selectedUsersCount > 0
                  ? `${selectedUsersCount} Users`
                  : "No Users"
              }}
            </span>
            <span>, </span>
            <span class="font-semibold">
              {{
                selectedTeamsCount > 0
                  ? `${selectedTeamsCount} Teams`
                  : "No Teams"
              }}
            </span>
            <span> have access</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import UsersTeamsSelectDropdown from "@/src/components/view_components/UsersTeamsSelectDropdown.vue";

const props = defineProps({
  actionKey: { type: String, required: true },
  conditionType: { type: String, default: null },
  assignedProperty: { type: Array, default: () => [] },
  specificUsers: { type: Array, default: () => [] },
  specificTeams: { type: Array, default: () => [] },
  objectProperties: { type: Array, default: () => [] },
  usersTeamsOptions: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["update", "remove"]);

const conditionTypeOptions = [
  { value: "user_in_role", label: "user in this role" },
  { value: "user_and_team", label: "user in this role and their team" },
  { value: "specific_users_teams", label: "specific user(s) and team(s)" },
];

const userTypeProperties = computed(() => {
  return props.objectProperties.filter(
    (property) => property.type === "user" || property.dataType === "user"
  );
});

const selectedUsersCount = computed(() => {
  return Array.isArray(props.specificUsers) ? props.specificUsers.length : 0;
});

const selectedTeamsCount = computed(() => {
  return Array.isArray(props.specificTeams) ? props.specificTeams.length : 0;
});

const usersAndTeamsModel = computed({
  get: () => {
    return [...props.specificUsers, ...props.specificTeams];
  },
  set: () => {},
});

const initialSelectionByTab = computed(() => {
  return {
    users: props.specificUsers || [],
    teams: props.specificTeams || [],
  };
});

const handleUpdate = (key, value) => {
  emit("update", { key, value });
};

const handlePropertyChange = (selectedValues) => {
  if (!props.conditionType) {
    emit("update", { key: "type", value: "user_in_role" });
  }

  if (selectedValues.includes("__select_all__")) {
    const allPropertySlugs = userTypeProperties.value.map((p) => p.slug);
    handleUpdate("property", allPropertySlugs);
  } else {
    handleUpdate("property", selectedValues);
  }
};

const handleRemove = () => {
  emit("remove");
};

const handleUsersTeamsChange = (data) => {
  if (data.byTab) {
    const userIds = data.byTab.users || [];
    const teamIds = data.byTab.teams || [];

    emit("update", { key: "users", value: userIds });
    emit("update", { key: "teams", value: teamIds });
  }
};

const handleTypeChange = (value) => {
  emit("update", { key: "type", value });
  if (value !== "specific_users_teams") {
    emit("update", { key: "users", value: [] });
    emit("update", { key: "teams", value: [] });
  }
};
</script>

<style scoped>
.crm_condition_select :deep(.ant-select-dropdown) {
  width: auto !important;
}

.select_all_option :deep(.ant-select-item-option-content) {
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 8px;
  margin-bottom: 4px;
}
</style>
